import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'The Science of Social Support',
    content: [
      '<strong>Learning Goal:</strong> Understand the three dimensions of MSPSS, distinguish between received and perceived support, and recognize how social support enhances well-being.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• The three dimensions of MSPSS support',
      '• Received vs. perceived social support',
      '• Social support as a stress buffer',
      '• Cultural influences on support expression',
      '<br/>',
      '<strong>Discussion:</strong> Social support isn\'t just about having people around–it\'s about the quality and perception of that support. Understanding these dimensions helps you build more effective support networks.'
    ]
  },
  {
    title: 'Significant Other Support',
    content: [
      'Significant Other support comes from your closest confidant, typically a romantic partner or best friend. This dimension focuses on:',
      '<br/>',
      '<strong>Emotional Intimacy:</strong>',
      '• Deep emotional connection and understanding',
      '• Feeling safe to express vulnerable feelings',
      '• Mutual trust and reliability',
      '• Being someone\'s priority during difficult times',
      '<br/>',
      '<strong>Characteristics:</strong>',
      '• Often involves one primary relationship',
      '• High level of emotional investment',
      '• Can provide the deepest level of support',
      '• May be absent for those who are single',
      '<br/>',
      '<strong>Examples:</strong>',
      '• "I know you\'re going through a tough time, and I\'m here for you no matter what"',
      '• Being the first person someone calls during a crisis',
      '• Providing comfort through physical presence and emotional availability'
    ]
  },
  {
    title: 'Family Support',
    content: [
      'Family support encompasses relationships with relatives and family members. This dimension emphasizes:',
      '<br/>',
      '<strong>Shared Responsibility:</strong>',
      '• Collective problem-solving and burden-sharing',
      '• Long-term commitment and loyalty',
      '• Intergenerational wisdom and guidance',
      '• Cultural and familial traditions',
      '<br/>',
      '<strong>Characteristics:</strong>',
      '• Often involves multiple family members',
      '• Can be complex due to family dynamics',
      '• May include extended family networks',
      '• Can be both supportive and challenging',
      '<br/>',
      '<strong>Examples:</strong>',
      '• Family members helping with childcare during emergencies',
      '• Sharing family responsibilities during crises',
      '• Drawing on family wisdom for decision-making',
      '• Maintaining family traditions that provide comfort'
    ]
  },
  {
    title: 'Friends Support',
    content: [
      'Friends support comes from peer relationships and social acquaintances. This dimension highlights:',
      '<br/>',
      '<strong>Empathy and Companionship:</strong>',
      '• Shared experiences and understanding',
      '• Social belonging and peer acceptance',
      '• Recreational and leisure activities',
      '• Diverse perspectives and social learning',
      '<br/>',
      '<strong>Characteristics:</strong>',
      '• Often involves multiple friendships',
      '• Can be more flexible and voluntary',
      '• May change over time and life stages',
      '• Provides social variety and new experiences',
      '<br/>',
      '<strong>Examples:</strong>',
      '• Friends providing distraction during stressful times',
      '• Social activities that maintain normalcy',
      '• Peer support for shared challenges',
      '• Expanding social circles through mutual friends'
    ]
  },
  {
    title: 'Received vs. Perceived Support',
    content: [
      'Understanding the difference between received and perceived support is crucial for socio-relational well-being:',
      '<br/>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">ASPECT</div>
          <div class="table-header means-cell">RECEIVED SUPPORT</div>
          <div class="table-header matters-cell">PERCEIVED SUPPORT</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Definition</div>
          <div class="table-cell means-cell">Actual help and assistance you get from others</div>
          <div class="table-cell matters-cell">Your belief that support is available if needed</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Measurement</div>
          <div class="table-cell means-cell">Tangible actions and help provided</div>
          <div class="table-cell matters-cell">Subjective feeling of being supported</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Impact on Health</div>
          <div class="table-cell means-cell">Direct assistance with problems</div>
          <div class="table-cell matters-cell">Stress buffering and preventive health benefits</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Examples</div>
          <div class="table-cell means-cell">Someone helping with groceries, lending money</div>
          <div class="table-cell matters-cell">Knowing you have people you can call in crisis</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Key Insight:</strong> Research shows that perceived support often has stronger protective effects on mental health than received support. The confidence that help is available can prevent stress from becoming overwhelming.'
    ]
  },
  {
    title: 'Social Support as a Stress Buffer',
    content: [
      'Social support acts as a buffer against stress through several mechanisms:',
      '<br/>',
      '<strong>Direct Effects:</strong>',
      '• Practical help reduces the burden of problems',
      '• Emotional support provides comfort and validation',
      '• Informational support offers advice and guidance',
      '<br/>',
      '<strong>Buffering Effects:</strong>',
      '• Changes how you appraise stressful situations',
      '• Provides coping resources and strategies',
      '• Enhances self-efficacy and confidence',
      '• Prevents stress from leading to depression',
      '<br/>',
      '<strong>Physiological Benefits:</strong>',
      '• Reduces cortisol (stress hormone) levels',
      '• Boosts oxytocin (bonding hormone) release',
      '• Strengthens immune system function',
      '• Improves cardiovascular health',
      '<br/>',
      '<strong>Research Evidence:</strong>',
      '• Studies show social support reduces depression risk by 40-60%',
      '• Strong social networks increase lifespan by 2-5 years',
      '• Perceived support protects against anxiety disorders',
      '• Social isolation increases mortality risk similar to smoking'
    ]
  },
  {
    title: 'Cultural Influences on Support',
    content: [
      'How social support is expressed varies across cultures and contexts:',
      '<br/>',
      '<strong>Individualistic Cultures (e.g., Western):</strong>',
      '• Emphasis on personal achievement and independence',
      '• Support often expressed verbally and emotionally',
      '• Friends and partners as primary support sources',
      '• Privacy and personal boundaries valued',
      '<br/>',
      '<strong>Collectivistic Cultures (e.g., Eastern):</strong>',
      '• Emphasis on group harmony and family obligations',
      '• Support often expressed through actions and presence',
      '• Extended family as primary support network',
      '• Group needs may take precedence over individual needs',
      '<br/>',
      '<strong>Cultural Variations:</strong>',
      '• Direct vs. indirect communication styles',
      '• Expressiveness of emotions',
      '• Role expectations in relationships',
      '• Stigma around seeking help',
      '<br/>',
      '<strong>Modern Considerations:</strong>',
      '• Digital communication changing support dynamics',
      '• Multicultural societies requiring cultural competence',
      '• Globalization blending cultural approaches',
      '• Online communities providing new support avenues'
    ]
  },
  {
    title: 'Support Circle Activity',
    content: [
      '<strong>Support Circle Diagram Exercise:</strong> Visualize your social support network.',
      '<br/>',
      '<strong>Step 1:</strong> Draw three concentric circles on paper.',
      '<strong>Step 2:</strong> Label them from inside out:',
      '• Inner Circle: Significant Other (closest confidant)',
      '• Middle Circle: Family (relatives and close family)',
      '• Outer Circle: Friends (acquaintances and broader network)',
      '<br/>',
      '<strong>Step 3:</strong> Write names or initials of people in each circle.',
      '<br/>',
      '<strong>Step 4:</strong> Reflect on these questions:',
      '• Which circle feels most populated and reliable?',
      '• Which circle feels lacking or needs strengthening?',
      '• How balanced is your support across the three areas?',
      '• What steps could you take to strengthen weaker areas?',
      '<br/>',
      '<strong>Takeaway:</strong> This visualization helps you assess the balance and strength of your support network. Most people benefit from having support across all three circles.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 2: The Science of Social Support! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about the MSPSS dimensions and social support science.',
        'The quiz will help you review key concepts about understanding different types of social support.'
      ]
    }
  }
];

const SocioRelationalModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('socioRelationalM2');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/socio-relational-module-2/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/socio-relational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left socio-relational-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">The Science of Social Support</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Understand the three dimensions of MSPSS, distinguish between received and perceived support, and recognize how social support enhances well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/socio-relational-module-2/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule2Page;

